/**
 * Lane grid for the Schedule Timeline (#946).
 *
 * Bars are opaque (mixed against the card, not translucent) so the amber
 * contention wash behind them cannot bleed through and desaturate them.
 */
import React from 'react';
import { formatDuration } from '@/utils/formatDuration';
import { cn } from '@/lib/utils';
import { niceStep, type ProjectedLane } from './scheduleProjection';
import { formatClock, formatDayLabel, formatInstant, zoneOffsetMs } from './scheduleTime';

const MIN = 60_000;

export interface ScheduleTimelineProps {
  lanes: ProjectedLane[];
  /** Visible range (equals the horizon unless zoomed). */
  viewStart: number;
  viewEnd: number;
  /** Horizon start — contention indices are relative to this. */
  windowStart: number;
  contention: Array<[number, number]>;
  displayTimezone: string;
  now: number;
  describeSchedule?: (lane: ProjectedLane) => string;
}

export const ScheduleTimeline: React.FC<ScheduleTimelineProps> = ({
  lanes,
  viewStart,
  viewEnd,
  windowStart,
  contention,
  displayTimezone,
  now,
  describeSchedule,
}) => {
  const span = viewEnd - viewStart;
  const pct = (ms: number) => ((ms - viewStart) / span) * 100;

  const step = niceStep(span);
  const showDay = step >= 6 * 60 * MIN;
  const nowLeft = now >= viewStart && now <= viewEnd ? pct(now) : null;

  const ticks: Array<{ at: number; left: number; major: boolean }> = [];
  // Align to the DISPLAY zone's boundaries, not UTC's and not the browser's.
  const offsetMs = zoneOffsetMs(viewStart, displayTimezone);
  for (let t = Math.ceil((viewStart + offsetMs) / step) * step - offsetMs; t <= viewEnd; t += step) {
    const label = formatClock(t, displayTimezone, { compact: true });
    ticks.push({ at: t, left: pct(t), major: label === '12 AM' });
  }

  /**
   * The backdrop is painted once per row (axis + every lane) so the wash, grid
   * rules and Now line run the full height of the chart. Only the axis copy
   * tags its washes as `contention-column`: they are one visual column each,
   * and tagging every row would make a count scale with the lane list rather
   * than with the number of contention windows.
   */
  const renderBackdrop = (tagWashes: boolean) => (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {contention.map(([a, b], i) => {
        const from = windowStart + a * MIN;
        const to = windowStart + b * MIN;
        if (to < viewStart || from > viewEnd) return null;
        const left = pct(Math.max(from, viewStart));
        const width = pct(Math.min(to, viewEnd)) - left;
        return (
          <div
            key={`c-${i}`}
            {...(tagWashes ? { 'data-testid': 'contention-column' } : {})}
            className="absolute inset-y-0 bg-warning/15 dark:bg-warning/10"
            style={{ left: `${left}%`, width: `${Math.max(width, 0.2)}%` }}
          />
        );
      })}
      {ticks.map((t) => (
        <div
          key={`r-${t.at}`}
          className={cn('absolute inset-y-0 w-px', t.major ? 'bg-muted-foreground/40' : 'bg-border')}
          style={{ left: `${t.left}%` }}
        />
      ))}
      {nowLeft !== null && (
        <div className="absolute inset-y-0 w-0.5 bg-foreground" style={{ left: `${nowLeft}%` }} />
      )}
    </div>
  );

  return (
    <div>
      {/* axis */}
      <div className="grid grid-cols-[236px_minmax(0,1fr)]">
        <div />
        <div className="relative h-8">
          {renderBackdrop(true)}
          {ticks.map((t) => {
            // The Now flag owns its slot — drop any tick it would sit on.
            if (nowLeft !== null && Math.abs(t.left - nowLeft) < 3.4) return null;
            const label =
              showDay && t.major
                ? formatDayLabel(t.at, displayTimezone)
                : formatClock(t.at, displayTimezone, { compact: true });
            return (
              <div
                key={`t-${t.at}`}
                className={cn(
                  'absolute top-1 -translate-x-1/2 whitespace-nowrap text-xs tabular-nums',
                  t.major ? 'font-medium text-foreground' : 'text-muted-foreground',
                )}
                style={{ left: `${t.left}%` }}
              >
                {label}
              </div>
            );
          })}
          {nowLeft !== null && (
            <div
              className="absolute top-1 rounded-sm bg-foreground px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-background"
              style={{ left: `${nowLeft}%` }}
            >
              Now
            </div>
          )}
        </div>
      </div>

      {/* lanes */}
      <div className="grid grid-cols-[236px_minmax(0,1fr)] border-t">
        {lanes.map((lane) => (
          <React.Fragment key={lane.configId}>
            <div className="flex h-[46px] items-center border-b pr-4">
              <div className="min-w-0">
                <div className="truncate font-medium">{lane.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {describeSchedule ? describeSchedule(lane) : lane.cron}
                  {lane.medianSeconds === null && ' · no history'}
                </div>
              </div>
            </div>
            <div className="relative h-[46px] border-b hover:bg-primary/5">
              {renderBackdrop(false)}
              <div className="absolute inset-0">
                {lane.runs.map((run, i) => {
                  if (run.end < viewStart || run.start > viewEnd) return null;
                  const left = pct(run.start);
                  const width = Math.max(pct(run.end) - left, 0.25);
                  const wide = width > 7 && !run.unknown;
                  return (
                    <React.Fragment key={`${lane.configId}-${i}`}>
                      <div
                        data-testid="schedule-bar"
                        data-config-id={lane.configId}
                        data-unknown={run.unknown ? 'true' : 'false'}
                        tabIndex={0}
                        title={`${lane.name} — starts ${formatInstant(run.start, displayTimezone)}`}
                        className={cn(
                          'absolute top-3 h-[22px] min-w-[3px] rounded-sm',
                          // Bars are OPAQUE — mixed against the card rather than
                          // an alpha tint — so the amber contention wash behind
                          // them cannot bleed through and desaturate the very
                          // bars that cause the contention.
                          run.unknown
                            ? 'border-l-2 border-muted-foreground bg-[color-mix(in_oklab,var(--color-muted-foreground)_32%,var(--color-card))] [mask-image:linear-gradient(to_right,#000_28%,transparent_100%)]'
                            : 'border-l-2 border-primary bg-[color-mix(in_oklab,var(--color-primary)_26%,var(--color-card))]',
                        )}
                        style={{ left: `${left}%`, width: `${width}%` }}
                      />
                      {wide && (
                        <div
                          className="pointer-events-none absolute top-4 text-[10px] tabular-nums text-muted-foreground"
                          style={{ left: `calc(${left}% + 7px)` }}
                        >
                          {formatDuration(lane.medianSeconds)}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
